/*
 * Cashmere Wolf — "Complete the Look" drawer sync.
 * Opens a separate panel to the LEFT of the stock Prestige cart drawer, in sync
 * with it, without modifying the cart. Observes #cart-drawer's `open` attribute
 * (Prestige DialogElement toggles it), measures the cart panel width from its open
 * shadow root, and loads recommendations for the last item in the cart via the
 * Product Recommendations API (explicit section_id so the panel can live on <body>).
 */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cart = document.getElementById('cart-drawer');
  var panel = document.getElementById('cw-look-drawer');
  if (!cart || !panel) return;

  // Share the appended cart drawer's stacking context.
  if (panel.parentElement !== document.body) document.body.appendChild(panel);

  var intent = panel.getAttribute('data-intent') || 'complementary';
  var limit = panel.getAttribute('data-limit') || '4';
  var hasRecs = false;
  var lastPid = null;

  function isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  function applyStacking() {
    var cz = parseInt(getComputedStyle(cart).zIndex, 10);
    if (isNaN(cz)) cz = 2000;
    // Above the cart's full-screen dim overlay so the panel is not darkened.
    // It sits to the left of the cart (no overlap) so it never covers it.
    panel.style.zIndex = String(cz + 1);

    if (!isMobile()) {
      var content = cart.shadowRoot && cart.shadowRoot.querySelector('[part="content"]');
      var w = content ? Math.round(content.getBoundingClientRect().width) : 432;
      panel.style.setProperty('--cw-cart-w', w + 'px');
    } else {
      panel.style.removeProperty('--cw-cart-w');
    }
  }

  function fetchCards(theIntent, lim, pid) {
    var url = root + 'recommendations/products?section_id=cw-look-drawer&intent=' +
      encodeURIComponent(theIntent) + '&limit=' + encodeURIComponent(lim) +
      '&product_id=' + encodeURIComponent(pid);
    return fetch(url).then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var list = doc.querySelector('[data-cw-look-list]');
      return list ? Array.prototype.slice.call(list.children) : [];
    });
  }

  function cardKey(card) {
    var a = card.querySelector('a[href]');
    return a ? a.getAttribute('href').split('?')[0] : null;
  }

  function loadRecs() {
    var want = parseInt(limit, 10) || 4;
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var items = (c && c.items) || [];
        if (!items.length) { hasRecs = false; return; }
        var pid = items[items.length - 1].product_id;
        if (pid === lastPid && hasRecs) return; // already loaded for this product
        lastPid = pid;

        return fetchCards(intent, want, pid)
          .then(function (cards) {
            if (cards.length >= want || intent === 'related') return cards.slice(0, want);
            // Top up to `want` with Related recommendations, skipping duplicates.
            return fetchCards('related', want + 6, pid).then(function (rel) {
              var seen = {};
              cards.forEach(function (card) { var k = cardKey(card); if (k) seen[k] = 1; });
              for (var i = 0; i < rel.length && cards.length < want; i++) {
                var k2 = cardKey(rel[i]);
                if (k2 && !seen[k2]) { seen[k2] = 1; cards.push(rel[i]); }
              }
              return cards;
            });
          })
          .then(function (cards) {
            var body = panel.querySelector('[data-cw-look-body]');
            if (cards && cards.length && body) {
              var wrap = document.createElement('div');
              wrap.className = 'cw-look__list';
              wrap.setAttribute('data-cw-look-list', '');
              cards.forEach(function (card) { wrap.appendChild(card); });
              body.innerHTML = '';
              body.appendChild(wrap);
              hasRecs = true;
            } else {
              hasRecs = false;
            }
          });
      })
      .catch(function () { hasRecs = false; });
  }

  var inner = panel.querySelector('.cw-look__inner');

  function openPanel() {
    applyStacking();
    panel.classList.add('is-open');
    if (inner) inner.setAttribute('aria-hidden', 'false');
  }

  function closePanel() {
    panel.classList.remove('is-open');
    if (inner) inner.setAttribute('aria-hidden', 'true');
  }

  // Escape closes the Look panel first (returns to the cart) when it is open.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && panel.classList.contains('is-open')) {
      e.stopPropagation();
      closePanel();
    }
  }, true);

  // --- Mobile in-cart trigger button ("Complete the Look") ---
  // On phones there is no room beside the cart, so (like ChinMounts) a button is
  // injected into the cart and the panel slides OVER the cart from the same side.
  var titleEl = panel.querySelector('.cw-look__title');
  var triggerLabel = titleEl ? titleEl.textContent.trim() : 'Complete the Look';

  function makeTrigger() {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'cw-look-trigger';
    b.setAttribute('data-cw-look-trigger', '');
    b.innerHTML = '<span>' + triggerLabel + '</span><span class="cw-look-trigger__chev" aria-hidden="true">›</span>';
    b.addEventListener('click', openPanel);
    return b;
  }
  function removeTrigger() {
    var ex = cart.querySelector('[data-cw-look-trigger]');
    if (ex) ex.remove();
  }
  function ensureTrigger() {
    if (!isMobile() || !hasRecs || !cart.hasAttribute('open')) { removeTrigger(); return; }
    if (cart.querySelector('[data-cw-look-trigger]')) return;
    var items = cart.querySelector('.cart-drawer__items');
    var btn = makeTrigger();
    if (items && items.parentNode) items.parentNode.insertBefore(btn, items);
    else cart.appendChild(btn);
  }

  // Back button on the panel returns to the cart (mobile) / closes it (desktop).
  // On mobile the full-screen panel can cause the cart dialog to light-dismiss, so
  // re-assert the cart after retracting the panel — back always lands on the cart.
  var backBtn = panel.querySelector('[data-cw-look-back]');
  if (backBtn) backBtn.addEventListener('click', function () {
    closePanel();
    setTimeout(function () {
      // If the cart is still visible, closePanel already revealed it — done.
      if (cart.hasAttribute('open') && getComputedStyle(cart).display !== 'none') return;
      // Otherwise the cart got hidden behind the full-screen panel (mobile): re-open it
      // cleanly. The delay before show() is required — calling show() too soon after
      // hide() races the drawer's own transition and leaves the host stuck at display:none.
      try { cart.hide(); } catch (e) {}
      setTimeout(function () {
        try { cart.show(); }
        catch (e) {
          var o = document.querySelector('a[href="/cart"], [aria-controls="cart-drawer"], a[href="#cart-drawer"]');
          if (o) o.click();
        }
      }, 400);
    }, 50);
  });

  // REVERSE-ORDER CLOSE: when the cart's own close controls (overlay or X) are
  // clicked while the Look is open, retract the Look FIRST, then close the cart.
  // Capture phase so we run before the cart's bound handlers; the cart stays in
  // place while the Look tucks back behind its edge (no mid-screen collapse).
  cart.addEventListener('click', function (e) {
    if (!cart.hasAttribute('open') || !panel.classList.contains('is-open')) return;
    var isClose = e.composedPath().some(function (el) {
      if (!el || el.nodeType !== 1) return false;
      var p = el.getAttribute && el.getAttribute('part');
      return (p && (p.indexOf('overlay') > -1 || p.indexOf('close-button') > -1)) ||
        el.tagName === 'DIALOG-CLOSE-BUTTON';
    });
    if (!isClose) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    closePanel();
    var delay = isMobile() ? 360 : 560;
    setTimeout(function () { try { cart.hide(); } catch (_) {} }, delay);
  }, true);

  function onCartOpen() {
    applyStacking();
    if (!isMobile() && hasRecs) openPanel(); // desktop: open now if cached
    loadRecs().then(function () {
      if (!cart.hasAttribute('open')) return;
      if (isMobile()) ensureTrigger();          // mobile: show trigger, don't auto-open
      else if (hasRecs) openPanel();            // desktop: auto-emerge beside the cart
    });
  }

  // SEQUENCE: open the Look panel only once the cart has finished sliding in.
  cart.addEventListener('dialog:after-show', onCartOpen);

  // Close with the cart.
  new MutationObserver(function () {
    if (!cart.hasAttribute('open')) { closePanel(); removeTrigger(); }
  }).observe(cart, { attributes: true, attributeFilter: ['open'] });
  cart.addEventListener('dialog:after-hide', function () { closePanel(); removeTrigger(); });

  // Refresh recommendations + re-inject the trigger when the cart contents change
  // (a cart re-render wipes injected nodes).
  var t;
  new MutationObserver(function () {
    clearTimeout(t);
    t = setTimeout(function () {
      if (!cart.hasAttribute('open')) return;
      loadRecs().then(function () {
        if (!cart.hasAttribute('open')) return;
        if (isMobile()) ensureTrigger();
        else if (hasRecs) openPanel();
        else closePanel();
      });
    }, 200);
  }).observe(cart, { childList: true, subtree: true });

  window.addEventListener('resize', function () {
    if (panel.classList.contains('is-open')) applyStacking();
    if (cart.hasAttribute('open')) {
      if (isMobile()) ensureTrigger();
      else { removeTrigger(); } // desktop: no trigger; panel auto-shows via onCartOpen on next open
    }
  });

  loadRecs(); // prefetch so the first open is instant
})();
